import React, { useEffect, useMemo, useState } from "react";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { apiService } from "../services/apiService";
import { workflowService } from "../services/workflowService";
import { useNavigate } from "react-router-dom";
import { Workflow } from "../types/workflow";
import PaymentSuccessModal from "../components/common/PaymentSuccessModal";
import { usePaymentSuccess } from "../hooks/usePaymentSuccess";
import {
  StatCard,
  ScheduledWorkflowCard,
  WorkflowEmptyState,
} from "../components/dashboard";
import { PageHeader } from "../components/common";
import WorkflowEditModal from "../components/workflow/WorkflowEditModal";
import { Button } from "../components/ui/Button";
import { formatDateTime, formatDate, formatChartDate, formatChartLabel } from "../utils/dateFormatters";
import {
  FiUsers,
  FiMail,
  FiSend,
  FiRefreshCw,
  FiTrendingUp,
  FiBarChart2,
  FiLink,
  FiSettings,
  FiActivity,
  FiArrowRight,
  FiZap,
  FiPlus,
} from "react-icons/fi";

// Type-safe icon wrappers
const UsersIcon = FiUsers as React.FC<React.SVGProps<SVGSVGElement>>;
const SendIcon = FiSend as React.FC<React.SVGProps<SVGSVGElement>>;
const RefreshIcon = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;
const TrendingUpIcon = FiTrendingUp as React.FC<React.SVGProps<SVGSVGElement>>;
const BarChart2Icon = FiBarChart2 as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const SettingsIcon = FiSettings as React.FC<React.SVGProps<SVGSVGElement>>;
const ActivityIcon = FiActivity as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowRightIcon = FiArrowRight as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const MailIcon = FiMail as React.FC<React.SVGProps<SVGSVGElement>>;

// Delete confirmation modal component
interface DeleteModalProps {
  workflow: Workflow;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  workflow,
  onCancel,
  onConfirm,
  isDeleting,
}) => (
  <div className="su-delete-modal-overlay" onClick={onCancel}>
    <div className="su-delete-modal" onClick={(e) => e.stopPropagation()}>
      <h3 className="su-delete-modal-title">Delete Workflow</h3>
      <p className="su-delete-modal-message">
        Are you sure you want to delete &quot;{workflow.name}&quot;? This action cannot be
        undone.
      </p>
      <div className="su-delete-modal-actions">
        <button
          className="su-delete-modal-cancel"
          onClick={onCancel}
          disabled={isDeleting}
        >
          Cancel
        </button>
        <button
          className="su-delete-modal-confirm"
          onClick={onConfirm}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const { user, isLoading, error, canAccessApp } = useAuthStatus();
  const navigate = useNavigate();

  // Payment success modal handling
  const { showModal: showPaymentSuccessModal, planTier: paymentPlanTier, closeModal: closePaymentSuccessModal } = usePaymentSuccess();

  // Local dashboard data state
  const [loading, setLoading] = useState<boolean>(true);
  const [overview, setOverview] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Workflow management state
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalMode, setEditModalMode] = useState<"edit" | "preview">("edit");
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch overview only when authenticated and app is accessible
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const resp = await apiService.getDashboardOverview();
      setOverview(resp.data);
    } catch (e: any) {
      setLoadError(e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading || error || !canAccessApp) return;
    loadDashboard();
  }, [isLoading, error, canAccessApp]);

  // Extract data from overview
  const summary = overview?.summary || {};
  const sendTrend = overview?.charts?.send_trend_30d || [];
  const integrations = overview?.integrations || [];
  const workflows: Workflow[] = overview?.workflows || [];
  const insights = overview?.insights_highlights || [];
  const recent = overview?.recent_activity || [];

  // Filter active scheduled workflows (not paused)
  const scheduledWorkflows = useMemo(() => {
    return workflows.filter(
      (w) => w.status === "active" && w.schedule_config && w.schedule_enabled !== false
    );
  }, [workflows]);

  // Calculate total recipients from scheduled workflows
  const totalRecipients = useMemo(() => {
    // For now, use contacts count from summary
    // In production, this should sum recipients from each workflow's contact list
    return summary.contacts || 0;
  }, [summary]);

  const isOverviewLoading = loading && !overview;

  // Provider icon helper
  const providerIcon = (provider: string) => {
    const p = (provider || "").toLowerCase();
    if (p.includes("github")) return <ActivityIcon className="h-4 w-4" aria-hidden />;
    if (p.includes("gitlab")) return <ActivityIcon className="h-4 w-4" aria-hidden />;
    if (p.includes("slack")) return <ActivityIcon className="h-4 w-4" aria-hidden />;
    return <LinkIcon className="h-4 w-4" aria-hidden />;
  };

  // Workflow action handlers
  const handlePauseToggle = async (workflowId: string, currentlyPaused: boolean) => {
    try {
      await workflowService.updateWorkflow(workflowId, {
        schedule_enabled: currentlyPaused, // Toggle: if paused, enable; if enabled, pause
      });
      // Refresh dashboard data
      loadDashboard();
    } catch (err) {
      console.error("Failed to toggle workflow:", err);
    }
  };

  const handleEdit = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setEditModalMode("edit");
    setShowEditModal(true);
  };

  const handleView = (workflow: Workflow) => {
    // Open the modal in preview mode (read-only)
    setSelectedWorkflow(workflow);
    setEditModalMode("preview");
    setShowEditModal(true);
  };

  const handleDeleteClick = (workflow: Workflow) => {
    setWorkflowToDelete(workflow);
  };

  const handleDeleteConfirm = async () => {
    if (!workflowToDelete) return;
    
    setIsDeleting(true);
    try {
      await workflowService.deleteWorkflow(workflowToDelete.id);
      setWorkflowToDelete(null);
      loadDashboard();
    } catch (err) {
      console.error("Failed to delete workflow:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Get recipient count for a workflow (placeholder - would need API update)
  const getRecipientCount = (workflow: Workflow): number => {
    // This would ideally come from the API based on contact_list_id
    // For now, return a placeholder or the contact count
    return summary.contacts || 0;
  };

  // Early returns
  if (error) {
    return (
      <div className="p-4">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 text-red-600">
          Authentication Error
        </h1>
        <p className="text-red-500 text-sm sm:text-base">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm sm:text-base"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!canAccessApp) {
    return (
      <div className="p-4">
        <h1 className="text-xl sm:text-2xl font-bold mb-4">Access Denied</h1>
        <p className="text-sm sm:text-base">
          You don&apos;t have access to this page. Please complete onboarding first.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-4">
        <h1 className="text-xl sm:text-2xl font-bold mb-2">Dashboard</h1>
        <p className="text-red-600 text-sm">{loadError}</p>
        <Button className="mt-3" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-[90rem] mx-auto py-8 px-6 lg:px-8 xl:px-12">
        {/* ============================================
            HEADER SECTION
            ============================================ */}
        <PageHeader
          title="Dashboard"
          subtitle="Manage your scheduled workflows and updates"
          action={
            <button
              className="su-btn-primary"
              onClick={() => navigate("/updates/new")}
            >
              <PlusIcon />
              <span>NEW UPDATE</span>
            </button>
          }
        />

        {/* ============================================
            STATS CARDS
            ============================================ */}
        <div className="su-stats-grid">
          <StatCard
            icon={<RefreshIcon />}
            label="Active Workflows"
            value={isOverviewLoading ? "—" : scheduledWorkflows.length}
            colorTheme="purple"
            isLoading={isOverviewLoading}
          />
          <StatCard
            icon={<SendIcon />}
            label="Emails Sent (Month)"
            value={isOverviewLoading ? "—" : summary.emails_last_30_days || 0}
            colorTheme="green"
            isLoading={isOverviewLoading}
          />
          <StatCard
            icon={<UsersIcon />}
            label="Total Recipients"
            value={isOverviewLoading ? "—" : totalRecipients}
            colorTheme="olive"
            isLoading={isOverviewLoading}
          />
        </div>

        {/* ============================================
            SCHEDULED WORKFLOWS SECTION
            ============================================ */}
        <div className="su-scheduled-workflows-section">
          <div className="su-scheduled-workflows-header">
            <h2 className="su-scheduled-workflows-title">Scheduled Workflows</h2>
            <span className="su-scheduled-workflows-count">
              {scheduledWorkflows.length} total
            </span>
          </div>

          {isOverviewLoading ? (
            // Loading skeleton
            <div className="space-y-4 pt-4">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-48 bg-gray-800 rounded" />
                      <div className="h-5 w-16 bg-gray-800 rounded" />
                      <div className="h-5 w-16 bg-gray-800 rounded" />
                    </div>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4].map((j) => (
                        <div key={j} className="h-10 w-10 bg-gray-800" />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j}>
                        <div className="h-3 w-16 bg-gray-800 rounded mb-2" />
                        <div className="h-4 w-24 bg-gray-800 rounded" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : scheduledWorkflows.length === 0 ? (
            <WorkflowEmptyState
              onCreateWorkflow={() => navigate("/updates/new")}
            />
          ) : (
            <div>
              {scheduledWorkflows.map((workflow) => (
                <ScheduledWorkflowCard
                  key={workflow.id}
                  workflow={workflow}
                  recipientCount={getRecipientCount(workflow)}
                  onPauseToggle={handlePauseToggle}
                  onEdit={handleEdit}
                  onView={handleView}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* ============================================
            LEGACY SECTIONS
            ============================================ */}
        <div className="su-legacy-sections">
          {/* Left Column */}
          <div className="su-legacy-left-column">
            {/* Email Performance Chart */}
            <div className="su-section">
              <div className="su-section-header">
                <h2 className="su-text-section-heading">
                  <TrendingUpIcon className="h-4 w-4" aria-hidden />
                  <span>Email performance (30d)</span>
                </h2>
                <button
                  className="su-text-link"
                  onClick={() => navigate("/email-updates")}
                >
                  <ActivityIcon className="h-4 w-4" aria-hidden />
                  <span>View Emails</span>
                </button>
              </div>
              <div className="mt-2">
                {isOverviewLoading ? (
                  <div className="h-52 w-full bg-gray-800 rounded animate-pulse" />
                ) : (
                  <>
                    <div className="flex gap-2">
                      {/* Y-axis labels */}
                      <div
                        className="flex flex-col justify-between text-xs text-gray-500 h-40"
                        style={{ minWidth: "30px" }}
                      >
                        {(() => {
                          const dataMax = Math.max(
                            1,
                            ...sendTrend.map((x: any) => x.sent || 0)
                          );
                          let yMax;
                          if (dataMax <= 5) yMax = 5;
                          else if (dataMax <= 10) yMax = 10;
                          else if (dataMax <= 20) yMax = 20;
                          else if (dataMax <= 25) yMax = 25;
                          else if (dataMax <= 50) yMax = 50;
                          else yMax = Math.ceil(dataMax / 50) * 50;

                          const step = yMax / 5;
                          return [5, 4, 3, 2, 1, 0].map((i) => (
                            <div key={i} className="text-right">
                              {step * i}
                            </div>
                          ));
                        })()}
                      </div>

                      {/* Chart with grid lines */}
                      <div className="flex-1 relative">
                        {/* Horizontal grid lines */}
                        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div
                              key={i}
                              className="border-t border-gray-800 border-dashed"
                            />
                          ))}
                        </div>

                        {/* Bars */}
                        <div
                          className="relative grid gap-1 items-end h-40 overflow-hidden"
                          style={{
                            gridTemplateColumns: `repeat(${
                              sendTrend.length || 30
                            }, minmax(0, 1fr))`,
                          }}
                        >
                          {sendTrend.map((p: any, idx: number) => {
                            const dataMax = Math.max(
                              1,
                              ...sendTrend.map((x: any) => x.sent || 0)
                            );
                            let yMax;
                            if (dataMax <= 5) yMax = 5;
                            else if (dataMax <= 10) yMax = 10;
                            else if (dataMax <= 20) yMax = 20;
                            else if (dataMax <= 25) yMax = 25;
                            else if (dataMax <= 50) yMax = 50;
                            else yMax = Math.ceil(dataMax / 50) * 50;

                            const height = ((p.sent || 0) / yMax) * 100;
                            return (
                              <div
                                key={idx}
                                className="bg-gradient-to-t from-blue-600 to-blue-400 cursor-pointer hover:from-blue-500 hover:to-blue-300"
                                style={{
                                  height: `${Math.max(height, p.sent > 0 ? 1 : 0)}%`,
                                  minHeight: p.sent > 0 ? "3px" : "0px",
                                }}
                                title={`${formatChartDate(p.date)}: ${p.sent} sent, ${p.delivered} delivered, ${p.opened} opened, ${p.clicked} clicked`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* X-axis labels */}
                    <div className="flex gap-2 mt-2">
                      <div style={{ minWidth: "30px" }} />
                      <div className="flex-1 relative h-5">
                        {sendTrend
                          .filter(
                            (_: any, i: number) =>
                              i % 5 === 0 || i === sendTrend.length - 1
                          )
                          .map((p: any) => {
                            const actualIndex = sendTrend.findIndex(
                              (x: any) => x.date === p.date
                            );
                            return (
                              <div
                                key={p.date}
                                className="absolute text-xs text-gray-500"
                                style={{
                                  left: `${
                                    (actualIndex / (sendTrend.length - 1 || 1)) *
                                    100
                                  }%`,
                                  transform: "translateX(-50%)",
                                }}
                              >
                                {formatChartLabel(p.date)}
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <div className="mt-2 text-xs text-gray-500">
                      Bars show emails sent per day. Hover for details.
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="su-section">
              <div className="su-section-header">
                <h2 className="su-text-section-heading">
                  <ActivityIcon className="h-4 w-4" aria-hidden />
                  <span>Recent activity</span>
                </h2>
                <button
                  className="su-text-link"
                  onClick={() => navigate("/insights")}
                >
                  <BarChart2Icon className="h-4 w-4" aria-hidden />
                  <span>Insights</span>
                </button>
              </div>
              <div className="su-activity-list">
                {isOverviewLoading && (
                  <div className="space-y-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="py-2 flex items-center">
                        <div className="h-4 w-4 bg-gray-800 rounded animate-pulse mr-3" />
                        <div className="flex-1">
                          <div className="h-4 w-40 bg-gray-800 rounded animate-pulse" />
                          <div className="h-3 w-28 bg-gray-800 rounded animate-pulse mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!isOverviewLoading && recent.length === 0 && (
                  <div className="text-sm text-gray-500 py-4">No recent activity</div>
                )}
                {!isOverviewLoading &&
                  recent.map((item: any) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className="su-activity-item"
                    >
                      <div className="flex items-start">
                        <div className="su-activity-icon">
                          {item.type === "insight" ? (
                            <BarChart2Icon className="h-4 w-4" />
                          ) : (
                            <MailIcon className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <div className="su-text-item-title">
                            {item.type === "insight"
                              ? item.title
                              : `Email update ${item.id.slice(0, 8)}`}
                          </div>
                          <div className="su-text-meta">
                            {formatDateTime(item.created_at)}
                          </div>
                        </div>
                      </div>
                      {item.type === "email" && (
                        <div className="text-xs text-gray-500">
                          <span className="mr-2">
                            {item?.meta?.delivered || 0} delivered
                          </span>
                          <span className="mr-2">
                            {item?.meta?.opened || 0} opened
                          </span>
                          <span>{item?.meta?.clicked || 0} clicked</span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="su-legacy-right-column">
            {/* Quick Actions */}
            <div className="su-section">
              <h2 className="su-text-section-heading mb-4">
                <ZapIcon className="h-4 w-4" aria-hidden />
                <span>Quick actions</span>
              </h2>
              <div className="space-y-2">
                <button
                  className="su-btn-quick-action"
                  onClick={() => navigate("/updates/new")}
                >
                  <ZapIcon className="h-4 w-4" aria-hidden />
                  <span>Send update now</span>
                </button>
                <button
                  className="su-btn-quick-action"
                  onClick={() => navigate("/integrations")}
                >
                  <LinkIcon className="h-4 w-4" aria-hidden />
                  <span>Connect integrations</span>
                </button>
              </div>
            </div>

            {/* Integrations */}
            <div className="su-section">
              <div className="su-section-header">
                <h2 className="su-text-section-heading">
                  <LinkIcon className="h-4 w-4" aria-hidden />
                  <span>Integrations</span>
                </h2>
                <button
                  className="su-text-link"
                  onClick={() => navigate("/integrations")}
                >
                  <SettingsIcon className="h-4 w-4" aria-hidden />
                  <span>Manage</span>
                </button>
              </div>
              <div className="space-y-2">
                {isOverviewLoading && (
                  <div className="space-y-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="su-integration-item">
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 bg-gray-800 rounded animate-pulse" />
                          <div className="h-4 w-24 bg-gray-800 rounded animate-pulse" />
                        </div>
                        <div className="h-3 w-20 bg-gray-800 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                )}
                {!isOverviewLoading && integrations.length === 0 && (
                  <div className="text-sm text-gray-500 py-2">
                    No integrations connected
                  </div>
                )}
                {!isOverviewLoading &&
                  integrations.map((i: any) => (
                    <div key={i.id} className="su-integration-item">
                      <div className="su-integration-name">
                        {providerIcon(i.provider)}
                        <span className="capitalize">{i.provider}</span>
                      </div>
                      <div className="su-text-meta flex items-center gap-2">
                        <span
                          className={
                            String(i.integration_status).toLowerCase() === "active"
                              ? "su-badge-status-active"
                              : "su-badge-status-paused"
                          }
                        >
                          {i.integration_status}
                        </span>
                        <span className="text-gray-500">
                          {formatDate(i.updated_at)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Recent Insights */}
            <div className="su-section">
              <div className="su-section-header">
                <h2 className="su-text-section-heading">
                  <BarChart2Icon className="h-4 w-4" aria-hidden />
                  <span>Recent insights</span>
                </h2>
                <button
                  className="su-text-link"
                  onClick={() => navigate("/insights")}
                >
                  <ArrowRightIcon className="h-4 w-4" aria-hidden />
                  <span>View all</span>
                </button>
              </div>
              <div className="space-y-2">
                {isOverviewLoading && (
                  <div className="space-y-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="py-2">
                        <div className="h-4 w-40 bg-gray-800 rounded animate-pulse" />
                        <div className="h-3 w-24 bg-gray-800 rounded animate-pulse mt-1" />
                      </div>
                    ))}
                  </div>
                )}
                {!isOverviewLoading && insights.length === 0 && (
                  <div className="text-sm text-gray-500 py-2">No insights yet</div>
                )}
                {!isOverviewLoading &&
                  insights.map((ins: any) => (
                    <div key={ins.id} className="su-insight-item">
                      <div className="su-text-item-title">{ins.title}</div>
                      <div className="su-text-meta">
                        {ins.category} • {formatDate(ins.created_at)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && selectedWorkflow && (
        <WorkflowEditModal
          workflow={selectedWorkflow}
          mode={editModalMode}
          onClose={() => {
            setShowEditModal(false);
            setSelectedWorkflow(null);
          }}
          onUpdated={() => {
            setShowEditModal(false);
            setSelectedWorkflow(null);
            loadDashboard();
          }}
          onDelete={async (workflowId: string) => {
            await workflowService.deleteWorkflow(workflowId);
            setShowEditModal(false);
            setSelectedWorkflow(null);
            loadDashboard();
          }}
        />
      )}

      {workflowToDelete && (
        <DeleteModal
          workflow={workflowToDelete}
          onCancel={() => setWorkflowToDelete(null)}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />
      )}

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showPaymentSuccessModal}
        onClose={closePaymentSuccessModal}
        planTier={paymentPlanTier}
      />
    </>
  );
};

export default Dashboard;
